import { createApi } from '@reduxjs/toolkit/query/react';
import { apiAuthenticatedQuery } from './baseQuery';
import {
  Driver,
  License,
  SkillLevel,
  driverDetailParam,
  getDriversRequest,
  getDriversResponse,
  putDriverRequestBody,
} from '../types/driver';
import { extinctFalsyFromParams } from './nullExtict';
import { errorTagTypes, handleErrorTags } from './handleErrorTags';

export const driverApi = createApi({
  baseQuery: apiAuthenticatedQuery,
  reducerPath: 'driver',
  tagTypes: ['Driver', ...errorTagTypes],
  endpoints: (builder) => ({
    getDrivers: builder.query<getDriversResponse, getDriversRequest>({
      query: (request) => ({
        url: `/drivers`,
        method: 'GET',
        params: extinctFalsyFromParams(request),
      }),
      providesTags: (res, error) => [
        ...(res?.drivers || []).map((c) => ({
          type: 'Driver' as const,
          id: c.userId,
        })),
        { type: 'Driver', id: 'List' },
        ...handleErrorTags(error),
      ],
    }),
    getDriverDetail: builder.query<Driver, driverDetailParam>({
      query: (param) => ({
        url: `/drivers/${param.userId}`,
        method: 'GET',
      }),
      providesTags: (_res, error) => handleErrorTags(error),
    }),
    putDriver: builder.mutation<
      void,
      { userId: number; data: putDriverRequestBody }
    >({
      query: ({ userId, data }) => ({
        url: `/drivers/${userId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_res, _err, args) => [
        { type: 'Driver', id: args.userId },
        { type: 'Driver', id: 'List' },
      ],
    }),
    deliteDriver: builder.mutation<void, number>({
      query: (userId) => ({
        url: `/drivers/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: 'Driver', id },
        { type: 'Driver', id: 'List' },
      ],
    }),
    getDriverSkillLevels: builder.query<SkillLevel[], void>({
      query: () => ({
        url: `/driver/skill-levels`,
        method: 'GET',
      }),
      providesTags: (_res, error) => handleErrorTags(error),
    }),
    getDriverLicenses: builder.query<License[], void>({
      query: () => ({
        url: `/driver/licenses`,
        method: 'GET',
      }),
      providesTags: (_res, error) => handleErrorTags(error),
    }),
    postRegisterCsvByDriver: builder.mutation<void, File>({
      query: (file) => {
        const body = new FormData();
        body.append('csv', file);
        body.append('type', 'driver');
        return {
          url: `/register-csv`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: [
        {
          type: 'Driver',
          id: 'List',
        },
      ],
    }),
  }),
});

export const {
  useGetDriversQuery,
  useGetDriverDetailQuery,
  usePutDriverMutation,
  useDeliteDriverMutation,
  useGetDriverSkillLevelsQuery,
  useGetDriverLicensesQuery,
  usePostRegisterCsvByDriverMutation,
} = driverApi;
